import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuth } from '@clerk/react';
import {
  Container,
  Card,
  Stack,
  Text,
  Title,
  Group,
  Badge,
  Loader,
  Alert,
  TextInput,
  Select,
  Button,
  NumberInput,
  SimpleGrid,
  Pagination,
  ActionIcon,
  Avatar,
  Modal,
  Chip
} from '@mantine/core';
import {
  IconSearch,
  IconExclamationCircle,
  IconRefresh,
  IconEye,
  IconShoppingCart,
  IconMessage,
  IconCrown,
  IconUser
} from '@tabler/icons-react';
import { useApi, mediaUrl } from '../../api';
import { addToCart } from '../../store/slices/cartSlice';
import { formatCents, formatCondition, getConditionColor, primaryImagePath } from './productDisplay';

// Convert a dollar amount from a NumberInput ('' | number) to integer cents
// for the min_price_cents / max_price_cents query params (contract §4.1).
const dollarsToCents = (value) =>
  value === '' || value === null || value === undefined
    ? undefined
    : Math.round(Number(value) * 100);

const ProductBrowse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Get seller filter from URL
  const sellerId = searchParams.get('seller');
  const sellerName = searchParams.get('seller_name');

  // Fetch the seller for the profile header (public schema, §3.5)
  const { data: sellerDetails } = useApi(
    `/api/v1/sellers/${sellerId ?? 0}`,
    undefined,
    { enabled: !!sellerId }
  );

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [condition, setCondition] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Popular-tag vocabulary for the filter chips (contract §4.1)
  const { data: tagsData } = useApi('/api/v1/products/tags');
  const popularTags = tagsData?.items ?? [];

  const { data, loading, error, refetch } = useApi(
    '/api/v1/products',
    {
      page: currentPage,
      limit: 12,
      search: searchTerm || undefined,
      condition: condition || undefined,
      min_price_cents: dollarsToCents(minPrice),
      max_price_cents: dollarsToCents(maxPrice),
      seller_id: sellerId || undefined,
      tag: activeTag || undefined,
    },
    // Keep the previous page's products visible while filters change
    { query: { placeholderData: (prev) => prev } }
  );

  const products = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const handleMessagePrincess = () => {
    if (!isSignedIn) {
      window.location.href = '/';
      return;
    }

    // Navigate to conversation with the seller's user ID (not seller profile ID)
    if (sellerDetails?.user_id) {
      navigate(`/messages/${sellerDetails.user_id}`);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    refetch();
  };

  // Single-select tag chip: click the active chip again to clear it
  const handleTagToggle = (tag) => {
    setCurrentPage(1);
    setActiveTag((prev) => (prev === tag ? '' : tag));
  };

  const handleAddToCart = (product) => {
    dispatch(addToCart({ product, quantity: 1 }));
  };

  const conditionOptions = [
    { value: '', label: 'All Conditions' },
    { value: 'new', label: 'New' },
    { value: 'like_new', label: 'Like New' },
    { value: 'used_excellent', label: 'Used - Excellent' },
    { value: 'used_good', label: 'Used - Good' },
    { value: 'used_fair', label: 'Used - Fair' },
    { value: 'refurbished', label: 'Refurbished' }
  ];

  if (loading && products.length === 0) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Princess Profile Header */}
        {sellerId && sellerDetails && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group align="flex-start">
                <Avatar size="xl" radius="md" color="pink">
                  <IconUser size="2rem" />
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Group gap="sm" mb="xs">
                    <Title order={2}>{sellerDetails.business_name}</Title>
                    {sellerDetails.is_verified && (
                      <Badge color="green" variant="light" size="md">
                        Verified Princess
                      </Badge>
                    )}
                  </Group>
                  <Text size="md" c="dimmed" mb="sm">
                    {sellerDetails.business_description || 'Welcome to my store!'}
                  </Text>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      {[sellerDetails.business_city, sellerDetails.business_state].filter(Boolean).join(', ')}
                    </Text>
                    <Text size="sm" c="dimmed">•</Text>
                    <Text size="sm" c="dimmed">
                      {sellerDetails.total_orders} orders • {sellerDetails.total_reviews} reviews
                    </Text>
                  </Group>
                </div>
              </Group>

              {/* Message Button */}
              <Button
                leftSection={<IconMessage size="1rem" />}
                onClick={handleMessagePrincess}
                variant="gradient"
                gradient={{ from: 'pink', to: 'grape' }}
              >
                Message the Princess
              </Button>
            </Stack>
          </Card>
        )}

        {/* Header */}
        <div>
          <Title order={1}>{sellerId ? `${sellerName || sellerDetails?.business_name || 'Princess'}'s Products` : 'Browse Products'}</Title>
          {sellerId && (
            <Group mt="sm">
              <Button
                variant="subtle"
                size="xs"
                onClick={() => window.location.href = '/products'}
              >
                ← Back to all products
              </Button>
            </Group>
          )}
        </div>

        {/* Search and Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group align="end" gap="md">
              <TextInput
                label="Search"
                placeholder="Search products..."
                leftSection={<IconSearch size="0.9rem" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1 }}
              />
              <Select
                label="Condition"
                placeholder="Any condition"
                value={condition}
                onChange={setCondition}
                data={conditionOptions}
                style={{ minWidth: 150 }}
              />
              <NumberInput
                label="Min Price"
                placeholder="$0"
                value={minPrice}
                onChange={setMinPrice}
                min={0}
                step={1}
                style={{ minWidth: 120 }}
              />
              <NumberInput
                label="Max Price"
                placeholder="$1000"
                value={maxPrice}
                onChange={setMaxPrice}
                min={0}
                step={1}
                style={{ minWidth: 120 }}
              />
              <Button onClick={handleSearch} loading={loading}>
                Search
              </Button>
              <ActionIcon onClick={() => refetch()} variant="light">
                <IconRefresh size="1rem" />
              </ActionIcon>
            </Group>

            {/* Popular tag filter chips (single-select; click again to clear) */}
            {popularTags.length > 0 && (
              <Group gap="xs" wrap="wrap">
                {popularTags.map(({ tag, count }) => (
                  <Chip
                    key={tag}
                    checked={activeTag === tag}
                    onChange={() => handleTagToggle(tag)}
                    color="pink"
                    size="sm"
                  >
                    {tag} ({count})
                  </Chip>
                ))}
              </Group>
            )}

            {error && (
              <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
                {error.message}
              </Alert>
            )}
          </Stack>
        </Card>

        {/* Products Grid */}
        {products.length === 0 && !loading ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Text c="dimmed" size="lg">No products found</Text>
              <Text c="dimmed" mt="xs">Try adjusting your search criteria</Text>
            </div>
          </Card>
        ) : (
          <SimpleGrid
            cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing="md"
          >
            {products.map((product) => {
              const imagePath = primaryImagePath(product.images);
              return (
              <Card
                key={product.id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Stack gap="sm" style={{ flex: 1 }}>
                  {/* Product Image Placeholder */}
                  <div
                    style={{
                      height: 200,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 8
                    }}
                  >
                    {imagePath ? (
                      <img
                        src={mediaUrl(imagePath)}
                        alt={product.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    ) : (
                      <Text c="dimmed" size="sm">No Image</Text>
                    )}
                  </div>

                  {/* Product Info */}
                  <div style={{ flex: 1 }}>
                    <Title
                      order={4}
                      lineClamp={2}
                      mb="xs"
                      component={Link}
                      to={`/products/${product.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.target.style.color = 'var(--mantine-color-pink-6)'}
                      onMouseLeave={(e) => e.target.style.color = 'inherit'}
                    >
                      {product.title}
                    </Title>

                    <Group gap="xs" mb="sm">
                      <Badge color={getConditionColor(product.condition)} variant="light" size="sm">
                        {formatCondition(product.condition)}
                      </Badge>
                      {product.quantity === 0 && (
                        <Badge color="red" variant="light" size="sm">
                          Out of Stock
                        </Badge>
                      )}
                    </Group>

                    <Text size="lg" fw={700} mb="xs">
                      {formatCents(product.price_cents)}
                    </Text>

                    <Text size="sm" c="dimmed" mb="xs">
                      By {product.seller_name}
                    </Text>

                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {product.views} views
                      </Text>
                      {/* Public products only come from approved + verified sellers */}
                      <Badge color="pink" variant="light" size="xs">
                        Verified Princess
                      </Badge>
                    </Group>
                  </div>

                  {/* Action Buttons */}
                  <Group gap="xs">
                    <Button
                      component={Link}
                      to={`/products/${product.id}`}
                      variant="light"
                      leftSection={<IconEye size="1rem" />}
                      style={{ flex: 1 }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="filled"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddToCart(product);
                      }}
                      disabled={product.quantity === 0}
                      leftSection={<IconShoppingCart size="0.9rem" />}
                      style={{ flex: 1 }}
                    >
                      Add to Cart
                    </Button>
                  </Group>
                </Stack>
              </Card>
              );
            })}
          </SimpleGrid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Group align="center">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Stack>

      {/* Upgrade Modal */}
      <Modal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to PayPiggiePro"
        centered
      >
        <Stack gap="md">
          <Group gap="sm">
            <IconCrown size="2rem" color="var(--mantine-color-yellow-6)" />
            <div>
              <Text fw={500}>Premium Feature</Text>
              <Text size="sm" c="dimmed">
                Messaging princesses is only available for PayPiggiePro subscribers
              </Text>
            </div>
          </Group>
          <Alert color="pink" variant="light">
            <Text size="sm">
              Upgrade to PayPiggiePro to unlock:
            </Text>
            <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              <li>Direct messaging with all princesses</li>
              <li>Priority customer support</li>
              <li>Exclusive deals and discounts</li>
              <li>Early access to new products</li>
            </ul>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe Later
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: 'pink', to: 'grape' }}
              leftSection={<IconCrown size="1rem" />}
              onClick={() => window.location.href = '/subscription'}
            >
              Upgrade Now - $25/month
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ProductBrowse;
